import * as React from "react";
import { DEVELOPER_TYPE_SELECT_OPTIONS } from "../../constants/application";
import SubmitButton from "../submitButton/submitButton";
import { Form, Formik, Field } from "formik";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSelector, useDispatch } from "react-redux";
import { createGoLive, listGoLiveDeveloperTypes } from "../../Apis/goLive";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { getRouteUrl } from "../../routers";
import { ROUTE_APPLICATION_VIEW } from "../../routers/content/application";
import FaSpinner from "../faSpinner/faSpinner";
import SimpleSelect from "../select";
import useQuestionaire from "../onBoarding/hooks/useQuestionaire";

const goLiveFormSchema = Yup.object().shape({
  address: Yup.string().required("Required"),
  companyName: Yup.string().required("Required"),
  developerType: Yup.string().required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
  siteUrl: Yup.string().required("Required"),
  useCases: Yup.string().required("Required"),
});
const GoLiveForm = ({ applicationId, userType, organizationUuid }) => {
  const dispatch = useDispatch();
  let history = useHistory();
  const csrfToken = useSelector(
    ({ applicationUser }) => applicationUser?.token
  );

  const userId = useSelector(({ applicationUser }) => applicationUser?.uid);

  // Fetch developer types from API
  const { data: developerTypesData, isLoading: developerTypesLoading } =
    useQuery(["goLiveDeveloperTypes"], () =>
      listGoLiveDeveloperTypes(csrfToken, userId)
    );

  const redirect = () =>
    history.push(
      getRouteUrl(
        ROUTE_APPLICATION_VIEW,
        {
          applicationId: applicationId,
        },
        // The application page needs the organization to load; without it the organization
        // fetch fails (DP-422) and the user is bounced to the organizations list.
        { organization: organizationUuid, golive: true }
      )
    );

  const create = useMutation(
    (message) => createGoLive(csrfToken, message, userId),
    {
      onSuccess: () => redirect(),
      onError: (error) => {
        // Error will be displayed in the UI
        console.error("GoLive creation failed:", error);
      },
    }
  );

  const handleSubmit = (values) => {
    // Find the developer type GUID from the API response
    let developerTypeId = null;
    if (developerTypesData?.payload?.data) {
      const developerType = developerTypesData.payload.data.find(
        (dt) => dt.name === values.developerType
      );
      developerTypeId = developerType?.id;
    }

    // Generate title from applicationId and timestamp
    const timestamp = Math.round(new Date().getTime() / 1000);
    const title = `app-${applicationId}-${timestamp}`;

    const data = {
      title: title,
      address: values.address,
      applicationId: applicationId,
      companyName: values.companyName,
      goLiveDeveloperTypeId: developerTypeId,
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      siteUrlDownloadLocation: values.siteUrl,
      useCases: values.useCases,
      urlAlias: "",
      langcode: "en",
      status: true,
      sticky: false,
      promote: false,
      published: true,
    };

    create.mutate(data);
  };
  const { handleQuestionaire } = useQuestionaire();
  const handleChange = (field, setFieldValue) => {
    const { name, value } = field;
    setFieldValue([name], value);
    if (
      name === "developerType" &&
      value === "Company" &&
      userType !== "CORPORATE"
    ) {
      handleQuestionaire("OPEN");
    }
  };
  const displayError = (name, touched, errors) => {
    return (
      touched[name] &&
      errors[name] && (
        <div style={{ color: "red", margin: "2px" }}>{errors[name]}</div>
      )
    );
  };

  const defaultValue =
    userType === "DEVELOPER"
      ? DEVELOPER_TYPE_SELECT_OPTIONS[0]
      : userType === "CORPORATE"
      ? DEVELOPER_TYPE_SELECT_OPTIONS[1]
      : "";

  if (create.isLoading || developerTypesLoading) {
    return <FaSpinner loading={true} />;
  }

  return (
    <div>
      <Formik
        enableReinitialize={true}
        validationSchema={goLiveFormSchema}
        initialValues={{
          developerType: defaultValue.value || "",
          firstName: "",
          lastName: "",
          companyName: "",
          address: "",
          email: "",
          siteUrl: "",
          useCases: "",
        }}
        onSubmit={async (values) => {
          await handleSubmit(values);
        }}
      >
        {({ errors, touched, setFieldValue }) => (
          <Form>
            <div key={defaultValue.value}>
              <SimpleSelect
                id={"developerType"}
                defaultValue={defaultValue}
                placeholder={"Select type"}
                options={DEVELOPER_TYPE_SELECT_OPTIONS}
                onChange={(value) =>
                  handleChange(
                    { ...value, name: "developerType" },
                    setFieldValue
                  )
                }
              />
            </div>
            {displayError("developerType", touched, errors)}
            <div style={{ marginBottom: "4px" }} />
            <Field
              className="body_field"
              id="firstName"
              name="firstName"
              placeholder="Your first name"
              disabled={create.isLoading}
            />

            {displayError("firstName", touched, errors)}
            <Field
              className="body_field"
              id="lastName"
              name="lastName"
              placeholder="Your last name"
              disabled={create.isLoading}
            />

            {displayError("lastName", touched, errors)}
            <Field
              className="body_field"
              id="companyName"
              name="companyName"
              placeholder="Company name"
              disabled={create.isLoading}
            />

            {displayError("companyName", touched, errors)}
            <Field
              className="body_field"
              name="address"
              placeholder="Your address"
              disabled={create.isLoading}
            />

            {displayError("address", touched, errors)}
            <Field
              className="body_field"
              name="email"
              placeholder="Your email"
              disabled={create.isLoading}
            />

            {displayError("email", touched, errors)}
            <Field
              className="body_field"
              name="siteUrl"
              placeholder="Site URL / Download Location"
              disabled={create.isLoading}
            />

            {displayError("siteUrl", touched, errors)}
            <Field
              className="body_field"
              name="useCases"
              placeholder="Please describe some use cases for your application"
              component="textarea"
              rows="6"
              disabled={create.isLoading}
            />

            {displayError("useCases", touched, errors)}
            <SubmitButton
              disabled={create.isLoading}
              styles={{ marginTop: "8px" }}
              title="SUBMIT"
            />
          </Form>
        )}
      </Formik>
      {create.isError ? (
        <div
          style={{
            color: "red",
            margin: "8px",
            padding: "12px",
            backgroundColor: "#fee",
            border: "1px solid #fcc",
            borderRadius: "4px",
          }}
        >
          <strong>Error:</strong>{" "}
          {create.error?.message ||
            "Failed to create Go Live request. Please check your input and try again."}
        </div>
      ) : null}
    </div>
  );
};
export default GoLiveForm;
